/**
 * A wall clock in the branch's timezone, turned into the instant it names.
 *
 * ── The bug this exists to close ────────────────────────────────────────────
 *
 * The booking form used to build its instant with
 * `new Date(\`${day}T${clock}:00\`)`. A date string carrying no zone is parsed
 * as **the browser's** local time, while every other part of the calendar draws
 * in the **branch's** — `getUTCHours()` on the day grid, `timeZone` on the
 * month, week and mini-month. The two agree only while the person booking
 * happens to sit in the branch's zone, which is why it survived.
 *
 * Measured on a UTC branch from a GMT+3 browser: typing `19:30` stored `16:30Z`.
 * A receptionist reading a free 16:30 off the grid and typing 16:30 booked
 * 13:30 — three hours out, silently, with the form reporting success.
 *
 * **P2 Q8 already settled which zone wins**, and it is not the browser's: *"the
 * viewer's browser was rejected outright — it lets the same visit show two dates
 * for two staff."* This makes the write agree with the decision and with the
 * grid.
 *
 * ── Why the offset is measured rather than looked up ────────────────────────
 *
 * The browser has the whole IANA database behind `Intl`, and no other source of
 * one is worth shipping: a stored `+03:00` cannot follow daylight saving, which
 * is the same reasoning that put real zone names in the timezone picker rather
 * than fixed offsets.
 */

/**
 * How far the zone is from UTC at a given instant, in milliseconds.
 *
 * Formats the instant *as* the zone reads it, then treats those fields as UTC.
 * The difference between that and the real instant is the offset.
 */
function offsetMsAt(instant: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).formatToParts(instant);

  const field = (type: Intl.DateTimeFormatPartTypes): number =>
    Number(parts.find((part) => part.type === type)?.value ?? "0");

  // `hour12: false` reports midnight as 24 in some engines; `% 24` is the
  // documented way round it and is a no-op everywhere else.
  const asUtc = Date.UTC(
    field("year"),
    field("month") - 1,
    field("day"),
    field("hour") % 24,
    field("minute"),
    field("second"),
  );

  return asUtc - instant.getTime();
}

/**
 * `2026-09-15` + `16:30` in `Asia/Beirut` → the instant that is 13:30 UTC.
 *
 * **Two passes, because the offset depends on the answer.** Reading the clock as
 * UTC gives an instant that is wrong by roughly the offset; correcting by that
 * offset lands on — or very near — the right one. A second measurement catches
 * the case where the first landed the other side of a daylight-saving boundary,
 * and agreeing twice is the fixed point. A clock inside a spring-forward gap
 * names no instant at all; there the two disagree for ever and the corrected
 * reading is returned, which is the same forgiving choice `Temporal` makes.
 */
export function instantOfWallClock(day: string, clock: string, timeZone: string): Date {
  const asUtc = new Date(`${day}T${clock}:00Z`);
  if (Number.isNaN(asUtc.getTime())) return asUtc;

  const first = new Date(asUtc.getTime() - offsetMsAt(asUtc, timeZone));
  const second = new Date(asUtc.getTime() - offsetMsAt(first, timeZone));

  return second;
}

/**
 * The reverse: an instant, as the day and clock the branch reads it at.
 *
 * `13:30Z` in `Asia/Beirut` → `{ day: "2026-09-15", clock: "16:30" }`. Used to
 * put a server-proposed chain visit into the same two boxes a person types into
 * (STORY-131b), so an edited visit and an untouched one go back through
 * `instantOfWallClock` the same way.
 */
export function wallClockOfInstant(
  instant: Date,
  timeZone: string,
): { day: string; clock: string } {
  const local = new Date(instant.getTime() + offsetMsAt(instant, timeZone));
  const iso = local.toISOString();
  return { day: iso.slice(0, 10), clock: iso.slice(11, 16) };
}

/**
 * Today, as `YYYY-MM-DD`, **at the location** rather than at the reader's
 * browser (P2 Q8, QA third run). A receptionist checking from home the evening
 * before, three zones east, would otherwise open tomorrow's Home and calendar.
 */
export function todayIn(timeZone: string, now: Date = new Date()): string {
  return wallClockOfInstant(now, timeZone).day;
}

/** The browser's own zone — the stand-in only until the location's is known. */
export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}
